import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';

import { PageActionModalComponent } from '@clinic-platform/ui-components';

export type ClinicSettingsFilterState = {
  query: string;
  timeZoneMode: '' | 'inherit' | 'override';
  slotOverride: '' | 'inherit' | 'override';
  sort: 'nameAsc' | 'nameDesc' | 'cityAsc' | 'cityDesc' | 'workingDayAsc' | 'workingDayDesc';
};

@Component({
  selector: 'app-clinic-settings-filters-modal',
  standalone: true,
  imports: [
    PageActionModalComponent,
    ReactiveFormsModule,
  ],
  template: `
    <lib-page-action-modal
      [isOpen]="isOpen"
      title="Filters & Sort"
      [subtitle]="'Tune the ' + clinicLabelPluralLower + ' list'"
      width="720px"
      (close)="close.emit()"
    >
      <form [formGroup]="filtersForm" (ngSubmit)="applyFilters()" class="clinic-settings-filters__grid">
        <label>
          Search
          <input type="text" formControlName="query" placeholder="Name, city, address, timezone, UUID" data-modal-primary-focus="true" />
        </label>
        <label>
          Timezone
          <select formControlName="timeZoneMode">
            <option value="">All</option>
            <option value="inherit">Inherited</option>
            <option value="override">Clinic override</option>
          </select>
        </label>
        <label>
          Slot override
          <select formControlName="slotOverride">
            <option value="">All</option>
            <option value="inherit">Inherited</option>
            <option value="override">Clinic override</option>
          </select>
        </label>
        <label>
          Sort
          <select formControlName="sort">
            <option value="nameAsc">Name A-Z</option>
            <option value="nameDesc">Name Z-A</option>
            <option value="cityAsc">City A-Z</option>
            <option value="cityDesc">City Z-A</option>
            <option value="workingDayAsc">Earliest working day</option>
            <option value="workingDayDesc">Latest working day</option>
          </select>
        </label>

        <div class="clinic-settings-filters__actions">
          <button type="button" class="clinic-settings-filters__action" (click)="reset.emit()">Reset</button>
          <button type="submit" class="clinic-settings-filters__action clinic-settings-filters__action--primary">Apply Filters</button>
        </div>
      </form>
    </lib-page-action-modal>
  `,
  styles: [`
    .clinic-settings-filters__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      align-items: end;
    }

    .clinic-settings-filters__grid label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 13px;
      color: #374151;
      font-weight: 600;
    }

    .clinic-settings-filters__grid input,
    .clinic-settings-filters__grid select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 10px;
      box-sizing: border-box;
      font-size: 14px;
      color: #111827;
      background: #ffffff;
    }

    .clinic-settings-filters__actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
    }

    .clinic-settings-filters__action {
      height: 32px;
      border-radius: 8px;
      border: 1px solid #2563eb;
      background: #ffffff;
      color: #2563eb;
      padding: 0 10px;
      font-size: 13px;
      cursor: pointer;
    }

    .clinic-settings-filters__action--primary {
      background: #2563eb;
      color: #ffffff;
    }

    @media (max-width: 900px) {
      .clinic-settings-filters__grid {
        grid-template-columns: 1fr;
      }

      .clinic-settings-filters__actions {
        flex-direction: column-reverse;
      }

      .clinic-settings-filters__action {
        width: 100%;
      }
    }
  `],
})
export class ClinicSettingsFiltersModalComponent implements OnChanges
{
  @Input() public isOpen = false;
  @Input() public clinicLabelPluralLower = 'clinics';
  @Input() public filters: ClinicSettingsFilterState = getDefaultClinicSettingsFilters();

  @Output() public readonly close = new EventEmitter<void>();
  @Output() public readonly apply = new EventEmitter<ClinicSettingsFilterState>();
  @Output() public readonly reset = new EventEmitter<void>();

  private readonly formBuilder = inject(FormBuilder);

  protected readonly filtersForm = this.formBuilder.group({
    query: this.formBuilder.nonNullable.control(''),
    timeZoneMode: this.formBuilder.nonNullable.control<ClinicSettingsFilterState['timeZoneMode']>(''),
    slotOverride: this.formBuilder.nonNullable.control<ClinicSettingsFilterState['slotOverride']>(''),
    sort: this.formBuilder.nonNullable.control<ClinicSettingsFilterState['sort']>('nameAsc'),
  });

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes['isOpen']?.currentValue === true || changes['filters']) {
      this.filtersForm.reset({ ...this.filters }, { emitEvent: false });
    }
  }

  protected applyFilters(): void
  {
    const value = this.filtersForm.getRawValue();
    this.apply.emit({
      query: value.query.trim(),
      timeZoneMode: value.timeZoneMode,
      slotOverride: value.slotOverride,
      sort: value.sort,
    });
  }
}

export function getDefaultClinicSettingsFilters(): ClinicSettingsFilterState
{
  return {
    query: '',
    timeZoneMode: '',
    slotOverride: '',
    sort: 'nameAsc',
  };
}
